// Billing & plans (tenant admins), plan requests (platform admins) and "My businesses" (every member).
// A product only appears in the app switcher and in role permissions once the organization has it:
// see PlatformAPI OrganizationProductAccess.
import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import ScheduleOutlined from '@mui/icons-material/ScheduleOutlined';
import VerifiedOutlined from '@mui/icons-material/VerifiedOutlined';
import SwapHorizOutlined from '@mui/icons-material/SwapHorizOutlined';
import AddRounded from '@mui/icons-material/AddRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { errorMessage, HubEmpty, HubSection, HubTitle, LoadState, ProductMark, productMark, useHubLoad } from '../hub/HubUi';
import { usePermissions } from '../../core/authorization/PermissionProvider';
import { useAppSelector } from '../../app/store';
import {
  billingApi,
  formatDate,
  formatPrice,
  type Business,
  type Plan,
  type PlanRequest,
  type ProductPlans,
} from './billingApi';
import './billing.css';

const STATUS_TONE: Record<string, string> = {
  Pending: 'warn',
  Approved: 'ok',
  Rejected: 'bad',
  Cancelled: 'muted',
};

function StatusPill({ status, children }: { status: string; children?: string }) {
  return <span className={`bl-pill is-${STATUS_TONE[status] ?? 'muted'}`}>{children ?? status}</span>;
}

function periodLabel(period: string, t: (key: string, fallback: string) => string) {
  return period === 'Month' ? t('billing.perMonth', '/ month') : t('billing.perYear', '/ year');
}

/* -------------------------------------------------------------------------------------------- */
/* Billing & plans                                                                              */
/* -------------------------------------------------------------------------------------------- */

export function BillingPage() {
  const { t, i18n } = useTranslation();
  const { hasScope } = usePermissions();
  const plans = useHubLoad(useCallback((signal: AbortSignal) => billingApi.plans(signal), []));
  const requests = useHubLoad(useCallback((signal: AbortSignal) => billingApi.myRequests(signal), []));
  const [selected, setSelected] = useState<string | null>(null);
  const [requesting, setRequesting] = useState<{ product: ProductPlans; plan: Plan } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  if (!hasScope('platform.organization.manage'))
    return (
      <>
        <HubTitle title={t('billing.title', 'Billing & plans')} />
        <HubEmpty>{t('billing.adminOnly', 'Only company administrators can manage plans.')}</HubEmpty>
      </>
    );

  const products = plans.data ?? [];
  const current = products.find((p) => p.productCode === selected) ?? products[0];

  return (
    <div className="bl-page">
      <HubTitle title={t('billing.title', 'Billing & plans')} />
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2, borderRadius: 3 }}>
          {notice}
        </Alert>
      )}
      <LoadState data={plans.data} error={plans.error} reload={plans.reload} />
      {plans.data && products.length === 0 && <HubEmpty>{t('billing.noPlans', 'No plans are available yet.')}</HubEmpty>}
      {current && (
        <>
          <div className="bl-products" role="tablist" aria-label={t('billing.products', 'Products')}>
            {products.map((product) => {
              const on = product.productCode === current.productCode;
              return (
                <button
                  key={product.productCode}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  className={`bl-product${on ? ' on' : ''}`}
                  style={{ '--mark': productMark(product.productCode).hue } as CSSProperties}
                  onClick={() => setSelected(product.productCode)}
                >
                  <ProductMark code={product.productCode} size={36} />
                  <span className="bl-product-name">{product.displayName}</span>
                  {product.subscription?.isActive ? (
                    <span className="bl-dot is-ok" title={t('billing.active', 'Active')} />
                  ) : product.pendingRequest ? (
                    <span className="bl-dot is-warn" title={t('billing.pending', 'Pending review')} />
                  ) : null}
                </button>
              );
            })}
          </div>

          <ProductStatus product={current} />

          <div className="bl-plans" role="tabpanel">
            {current.plans.map((plan, index) => {
              const isCurrent = current.subscription?.isActive && current.subscription.planCode === plan.code;
              const isPending = current.pendingRequest?.planCode === plan.code;
              const featured = current.plans.length >= 3 ? index === 1 : index === current.plans.length - 1;
              return (
                <article
                  key={plan.code}
                  className={`bl-plan${featured ? ' is-featured' : ''}${isCurrent ? ' is-current' : ''}`}
                  style={{ animationDelay: `${index * 70}ms`, '--mark': productMark(plan.productCode).hue } as CSSProperties}
                >
                  {featured && (
                    <span className="bl-plan-badge">
                      <AutoAwesomeOutlined sx={{ fontSize: 14 }} aria-hidden /> {t('billing.popular', 'Most popular')}
                    </span>
                  )}
                  <h3>{plan.name}</h3>
                  {plan.description && <p className="bl-plan-desc">{plan.description}</p>}
                  <div className="bl-price">
                    <strong>{formatPrice(plan.price, plan.currencyCode, i18n.language)}</strong>
                    <span>{periodLabel(plan.billingPeriod, t)}</span>
                  </div>
                  <ul className="bl-features">
                    {plan.features.map((feature) => (
                      <li key={feature}>
                        <CheckRounded sx={{ fontSize: 18 }} aria-hidden />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    fullWidth
                    size="large"
                    disableElevation
                    variant={featured && !isCurrent ? 'contained' : 'outlined'}
                    disabled={Boolean(isCurrent || current.pendingRequest)}
                    onClick={() => setRequesting({ product: current, plan })}
                    sx={{ borderRadius: 3, mt: 'auto' }}
                  >
                    {isCurrent
                      ? t('billing.currentPlan', 'Current plan')
                      : isPending
                        ? t('billing.pending', 'Pending review')
                        : current.subscription?.isActive
                          ? t('billing.switchPlan', 'Switch to this plan')
                          : t('billing.choose', 'Choose plan')}
                  </Button>
                </article>
              );
            })}
          </div>
        </>
      )}

      <HubSection title={t('billing.history', 'Your requests')}>
        <LoadState data={requests.data} error={requests.error} reload={requests.reload} />
        {requests.data && requests.data.length === 0 && <HubEmpty>{t('billing.noRequests', 'No requests yet.')}</HubEmpty>}
        {requests.data && requests.data.length > 0 && (
          <div className="bl-list">
            {requests.data.map((request) => (
              <RequestRow
                key={request.id}
                request={request}
                action={
                  request.status === 'Pending' ? (
                    <Button
                      size="small"
                      color="inherit"
                      onClick={async () => {
                        try {
                          await billingApi.cancel(request.id);
                        } finally {
                          requests.reload();
                          plans.reload();
                        }
                      }}
                    >
                      {t('billing.cancel', 'Cancel')}
                    </Button>
                  ) : null
                }
              />
            ))}
          </div>
        )}
      </HubSection>

      {requesting && (
        <RequestDialog
          product={requesting.product}
          plan={requesting.plan}
          close={() => setRequesting(null)}
          done={() => {
            setRequesting(null);
            setNotice(t('billing.sent', 'Request sent. A platform administrator will review it shortly.'));
            plans.reload();
            requests.reload();
          }}
        />
      )}
    </div>
  );
}

function ProductStatus({ product }: { product: ProductPlans }) {
  const { t, i18n } = useTranslation();
  const subscription = product.subscription;
  if (product.pendingRequest)
    return (
      <div className="bl-status is-warn">
        <ScheduleOutlined aria-hidden />
        <span>
          {t('billing.pendingSince', 'Request waiting for review since')} {formatDate(product.pendingRequest.requestedAt, i18n.language)}
        </span>
      </div>
    );
  if (subscription?.isActive)
    return (
      <div className="bl-status is-ok">
        <VerifiedOutlined aria-hidden />
        <span>
          {subscription.expiresOn
            ? `${t('billing.activeUntil', 'Active until')} ${formatDate(subscription.expiresOn, i18n.language)}`
            : t('billing.activeIncluded', 'Active — included with your account')}
        </span>
      </div>
    );
  return (
    <div className="bl-status">
      <span>{t('billing.notEnabled', 'Not enabled yet. Choose a plan to start using')} {product.displayName}.</span>
    </div>
  );
}

function RequestRow({ request, action, admin }: { request: PlanRequest; action?: ReactNode; admin?: boolean }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="bl-row">
      <ProductMark code={request.productCode} size={40} />
      <div className="bl-row-main">
        <strong>{admin ? request.organizationName : request.planName}</strong>
        <span>
          {admin ? `${request.planName} · ` : ''}
          {formatPrice(request.price, request.currencyCode, i18n.language)} {periodLabel(request.billingPeriod, t)} ·{' '}
          {formatDate(request.requestedAt, i18n.language)}
          {admin ? ` · ${request.requestedBy || request.requestedByEmail}` : ''}
        </span>
        {request.note && <em>“{request.note}”</em>}
        {request.reviewNote && <em className="bl-review-note">{request.reviewNote}</em>}
        {request.status === 'Approved' && request.endsOn && (
          <span className="bl-row-dates">
            {formatDate(request.startsOn, i18n.language)} – {formatDate(request.endsOn, i18n.language)}
          </span>
        )}
      </div>
      <StatusPill status={request.status}>{t(`billing.status.${request.status}`, request.status)}</StatusPill>
      {action}
    </div>
  );
}

function RequestDialog({ product, plan, close, done }: { product: ProductPlans; plan: Plan; close: () => void; done: () => void }) {
  const { t, i18n } = useTranslation();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open onClose={busy ? undefined : close} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <ProductMark code={product.productCode} size={36} />
        {plan.name}
      </DialogTitle>
      <DialogContent>
        <div className="bl-dialog-price">
          <strong>{formatPrice(plan.price, plan.currencyCode, i18n.language)}</strong> {periodLabel(plan.billingPeriod, t)}
        </div>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          label={t('billing.note', 'Note for the platform team (optional)')}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          inputProps={{ maxLength: 500 }}
          multiline
          minRows={3}
          fullWidth
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close} disabled={busy} color="inherit">
          {t('billing.back', 'Back')}
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={busy}
          sx={{ borderRadius: 3 }}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await billingApi.request(plan.code, note.trim() || null);
              done();
            } catch (caught) {
              setError(errorMessage(caught));
              setBusy(false);
            }
          }}
        >
          {busy ? t('billing.sending', 'Sending…') : t('billing.send', 'Send request')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------------------------- */
/* Plan requests (platform administrators)                                                      */
/* -------------------------------------------------------------------------------------------- */

export function PlanRequestsPage() {
  const { t } = useTranslation();
  const { hasScope } = usePermissions();
  if (!hasScope('platform.organization.manage')) return <HubEmpty>{t('billing.adminOnly', 'Only company administrators can manage plans.')}</HubEmpty>;
  return <PlanManagementContent />;
}
function PlanManagementContent() {
  const { t } = useTranslation();
  const { hasScope } = usePermissions();
  const canManage = hasScope('platform.organization.provision');
  const subscriptions = useHubLoad(useCallback((signal: AbortSignal) => canManage ? billingApi.subscriptions(signal) : billingApi.ownSubscriptions(signal), [canManage]));
  const plans = useHubLoad(useCallback((signal: AbortSignal) => billingApi.plans(signal), []));
  const [editing, setEditing] = useState<Plan | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (<div className="bl-page">
    <HubTitle title={t('billing.managementTitle', 'Plans & subscriptions')} />
    {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      <HubSection title={t('billing.priceList', 'Price list')}>
        <LoadState data={plans.data} error={plans.error} reload={plans.reload} />
        {plans.data && (
          <div className="bl-pricelist">
            {plans.data.map((product) => (
              <div key={product.productCode} className="bl-pricelist-group">
                <div className="bl-pricelist-head">
                  <ProductMark code={product.productCode} size={32} />
                  <strong>{product.displayName}</strong>
                </div>
                {product.plans.map((plan) => (
                  <button key={plan.code} type="button" className="bl-pricelist-row" disabled={!canManage} onClick={() => setEditing(plan)}>
                    <span>{plan.name}</span>
                    <strong>{formatPrice(plan.price, plan.currencyCode)}</strong>
                    {canManage && <EditOutlined sx={{ fontSize: 16 }} aria-label={t('billing.edit', 'Edit')} />}
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}
      </HubSection>

      <HubSection title={t('billing.subscriptions', 'Active products by company')}>
        <LoadState data={subscriptions.data} error={subscriptions.error} reload={subscriptions.reload} />
        {subscriptions.data && subscriptions.data.length === 0 && <HubEmpty>{t('billing.noSubscriptions', 'No company has a product yet.')}</HubEmpty>}
        {subscriptions.data && subscriptions.data.length > 0 && (
          <div className="bl-list">
            {subscriptions.data.map((row) => (
              <div key={`${row.organizationId}-${row.productCode}`} className="bl-row">
                <ProductMark code={row.productCode} size={40} />
                <div className="bl-row-main">
                  <strong>{row.organizationName}</strong>
                  <span>
                    {row.productName} · {t(`billing.source.${row.source}`, row.source)}
                    {row.expiresOn ? ` · ${t('billing.until', 'until')} ${formatDate(row.expiresOn)}` : ''}
                  </span>
                </div>
                <StatusPill status={row.isActive ? 'Approved' : 'Cancelled'}>
                  {row.isActive ? t('billing.active', 'Active') : t('billing.inactive', 'Inactive')}
                </StatusPill>
                {canManage && row.isActive && (
                  <Button
                    size="small"
                    color="inherit"
                    onClick={async () => {
                      if (!window.confirm(t('billing.disableConfirm', 'Disable this product for the company?'))) return;
                      try {
                        await billingApi.disable(row.organizationId, row.productCode);
                      } catch (caught) {
                        setError(errorMessage(caught));
                      } finally {
                        subscriptions.reload();
                      }
                    }}
                  >
                    {t('billing.disable', 'Disable')}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </HubSection>

      {editing && (
        <PlanDialog
          plan={editing}
          close={() => setEditing(null)}
          done={() => {
            setEditing(null);
            plans.reload();
          }}
        />
      )}
    </div>
  );
}


export function OrganizationRequestsPage() {
  const { t } = useTranslation();
  const { hasScope } = usePermissions();
  const isSuperAdmin = useAppSelector((state) => state.session.snapshot.isSuperAdmin === true);
  if (!isSuperAdmin || !hasScope('platform.organization.provision')) return <HubEmpty>{t('billing.requestsRestricted', 'Organization requests are available only to SuperAdmin.')}</HubEmpty>;
  return <OrganizationRequestsContent />;
}
function OrganizationRequestsContent() {
  const { t } = useTranslation();
  const [status, setStatus] = useState('Pending');
  const requests = useHubLoad(useCallback((signal: AbortSignal) => billingApi.allRequests(status, signal), [status]));
  const [review, setReview] = useState<{ request: PlanRequest; approve: boolean } | null>(null);
  const pendingCount = status === 'Pending' ? requests.data?.length ?? 0 : null;
  return <div className="bl-page">
    <HubTitle title={t('billing.organizationRequests', 'Organization requests')} action={
      <TextField select size="small" value={status} onChange={event => setStatus(event.target.value)}>
        {['Pending', 'Approved', 'Rejected', 'Cancelled', ''].map(value => <MenuItem key={value || 'all'} value={value}>
          {value ? t(`billing.status.${value}`, value) : t('billing.all', 'All')}
        </MenuItem>)}
      </TextField>} />
      <LoadState data={requests.data} error={requests.error} reload={requests.reload} />
      {requests.data && requests.data.length === 0 && (
        <HubEmpty>{status === 'Pending' ? t('billing.queueEmpty', 'All caught up — no requests waiting.') : t('billing.noRequests', 'No requests yet.')}</HubEmpty>
      )}
      {pendingCount !== null && pendingCount > 0 && (
        <p className="bl-count">
          {pendingCount} {t('billing.waiting', 'waiting for review')}
        </p>
      )}
      {requests.data && requests.data.length > 0 && (
        <div className="bl-list">
          {requests.data.map((request) => (
            <RequestRow
              key={request.id}
              admin
              request={request}
              action={
                request.status === 'Pending' ? (
                  <div className="bl-row-actions">
                    <Button size="small" color="inherit" onClick={() => setReview({ request, approve: false })}>
                      {t('billing.reject', 'Reject')}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      disableElevation
                      sx={{ borderRadius: 2.5 }}
                      onClick={() => setReview({ request, approve: true })}
                    >
                      {t('billing.approve', 'Approve')}
                    </Button>
                  </div>
                ) : null
              }
            />
          ))}
        </div>
      )}

      {review && (
        <ReviewDialog
          request={review.request}
          approve={review.approve}
          close={() => setReview(null)}
          done={() => {
            setReview(null);
            requests.reload();
          }}
        />
      )}
  </div>;
}

function ReviewDialog({ request, approve, close, done }: { request: PlanRequest; approve: boolean; close: () => void; done: () => void }) {
  const { t, i18n } = useTranslation();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open onClose={busy ? undefined : close} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle>{approve ? t('billing.approveTitle', 'Approve request') : t('billing.rejectTitle', 'Reject request')}</DialogTitle>
      <DialogContent>
        <div className="bl-review-summary">
          <ProductMark code={request.productCode} size={40} />
          <div>
            <strong>{request.organizationName}</strong>
            <span>
              {request.planName} · {formatPrice(request.price, request.currencyCode, i18n.language)}
            </span>
          </div>
        </div>
        {approve && (
          <p className="bl-help">{t('billing.approveHelp', 'The product is enabled for the company immediately, for one billing period.')}</p>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          label={t('billing.reviewNote', 'Message to the company (optional)')}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          inputProps={{ maxLength: 500 }}
          multiline
          minRows={2}
          fullWidth
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close} disabled={busy} color="inherit">
          {t('billing.back', 'Back')}
        </Button>
        <Button
          variant="contained"
          disableElevation
          color={approve ? 'primary' : 'error'}
          disabled={busy}
          sx={{ borderRadius: 3 }}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await (approve ? billingApi.approve : billingApi.reject)(request.id, note.trim() || null);
              done();
            } catch (caught) {
              setError(errorMessage(caught));
              setBusy(false);
            }
          }}
        >
          {approve ? t('billing.approve', 'Approve') : t('billing.reject', 'Reject')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PlanDialog({ plan, close, done }: { plan: Plan; close: () => void; done: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ ...plan, featuresText: plan.features.join('\n') });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog open onClose={busy ? undefined : close} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle>{t('billing.editPlan', 'Edit plan')}</DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 2, pt: '8px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label={t('billing.name', 'Name')} value={form.name} inputProps={{ maxLength: 100 }}
          onChange={(event) => setForm({ ...form, name: event.target.value })} />
        <TextField label={t('billing.description', 'Description')} value={form.description ?? ''} inputProps={{ maxLength: 400 }}
          onChange={(event) => setForm({ ...form, description: event.target.value })} />
        <div className="bl-form-row">
          <TextField label={t('billing.price', 'Price')} type="number" value={form.price} inputProps={{ min: 0, step: 1 }}
            onChange={(event) => setForm({ ...form, price: Number(event.target.value) })} />
          <TextField select label={t('billing.period', 'Period')} value={form.billingPeriod}
            onChange={(event) => setForm({ ...form, billingPeriod: event.target.value as Plan['billingPeriod'] })}>
            <MenuItem value="Year">{t('billing.year', 'Year')}</MenuItem>
            <MenuItem value="Month">{t('billing.month', 'Month')}</MenuItem>
          </TextField>
        </div>
        <TextField label={t('billing.features', 'Features (one per line)')} value={form.featuresText} multiline minRows={3}
          onChange={(event) => setForm({ ...form, featuresText: event.target.value })} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={close} disabled={busy} color="inherit">
          {t('billing.back', 'Back')}
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={busy}
          sx={{ borderRadius: 3 }}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const features = form.featuresText.split('\n').map((line) => line.trim()).filter(Boolean);
              await billingApi.updatePlan({ ...form, features });
              done();
            } catch (caught) {
              setError(errorMessage(caught));
              setBusy(false);
            }
          }}
        >
          {t('billing.save', 'Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------------------------- */
/* My businesses                                                                                */
/* -------------------------------------------------------------------------------------------- */

/** Grid of the companies the caller belongs to and the products each one has. */
export function BusinessesGrid() {
  const { t, i18n } = useTranslation();
  const { hasScope } = usePermissions();
  const load = useHubLoad(useCallback((signal: AbortSignal) => billingApi.businesses(signal), []));
  const canBill = hasScope('platform.organization.manage');
  const businesses = useMemo(() => load.data ?? [], [load.data]);

  if (!load.data) return <LoadState data={load.data} error={load.error} reload={load.reload} />;
  if (businesses.length === 0)
    return <HubEmpty>{t('businesses.none', 'You are not a member of any business yet.')}</HubEmpty>;

  return (
    <div className="bz-grid">
      {businesses.map((business, index) => (
        <BusinessCard key={business.id} business={business} index={index} canBill={canBill && business.isActive} language={i18n.language} />
      ))}
    </div>
  );
}

function BusinessCard({ business, index, canBill, language }: { business: Business; index: number; canBill: boolean; language: string }) {
  const { t } = useTranslation();
  const canEditProfiles = usePermissions().can('platform.company', 'read');
  const active = business.products.filter((p) => p.isActive);
  return (
    <article className={`bz-card${business.isActive ? ' is-current' : ''}`} style={{ animationDelay: `${index * 60}ms` }}>
      <header className="bz-head">
        <span className="bz-avatar" aria-hidden>
          {business.name.slice(0, 2).toUpperCase()}
        </span>
        <div className="bz-who">
          <strong>
            {business.name}
            {business.isVerified && <VerifiedOutlined sx={{ fontSize: 16, color: 'var(--primary)' }} aria-label="Verified" />}
          </strong>
          <span>{[business.city, business.roles.join(', ')].filter(Boolean).join(' · ') || business.code}</span>
        </div>
        {business.isActive ? (
          <span className="bl-pill is-ok">{t('businesses.current', 'Current')}</span>
        ) : (
          <Button component={Link} to="/switch" size="small" startIcon={<SwapHorizOutlined />} sx={{ borderRadius: 2.5 }}>
            {t('businesses.open', 'Open')}
          </Button>
        )}
      </header>

      <div className="bz-products">
        {business.products.length === 0 && <span className="bz-none">{t('businesses.noProducts', 'No products yet')}</span>}
        {business.products.map((product) => (
          <div
            key={product.code}
            className={`bz-product${product.isActive ? '' : ' is-off'}`}
            style={{ '--mark': productMark(product.code).hue } as CSSProperties}
            title={
              !product.isActive
                ? t('businesses.expired', 'Expired or disabled')
                : !product.isAssigned
                  ? t('businesses.notAssigned', 'Not assigned to you')
                  : undefined
            }
          >
            <ProductMark code={product.code} size={34} />
            <div>
              <strong>{product.displayName}</strong>
              <span>
                {!product.isActive
                  ? t('businesses.inactive', 'Inactive')
                  : product.expiresOn
                    ? `${t('billing.until', 'until')} ${formatDate(product.expiresOn, language)}`
                    : !product.isAssigned
                      ? t('businesses.notAssigned', 'Not assigned to you')
                      : t('billing.active', 'Active')}
              </span>
              {/* Each product is its own business with its own organization profile (PlatformDB 056). */}
              {canEditProfiles && business.isActive && product.isActive && (
                <Link className="bz-profile" to={`/app/organization-profiles?product=${encodeURIComponent(product.code)}`}>
                  {t('businesses.editProfile', 'Edit profile')}
                </Link>
              )}
            </div>
          </div>
        ))}
        {business.pendingRequests.map((pending) => (
          <div key={`p-${pending.productCode}`} className="bz-product is-pending">
            <ProductMark code={pending.productCode} size={34} />
            <div>
              <strong>{pending.productCode.charAt(0) + pending.productCode.slice(1).toLowerCase()}</strong>
              <span>{t('billing.pending', 'Pending review')}</span>
            </div>
          </div>
        ))}
        {canBill && (
          <Link to="/app/billing" className="bz-add">
            <AddRounded aria-hidden />
            <span>{t('businesses.addProduct', 'Add product')}</span>
          </Link>
        )}
      </div>
      {active.length > 0 && (
        <footer className="bz-foot">
          {active.length} {active.length === 1 ? t('businesses.product', 'product') : t('businesses.products', 'products')}
        </footer>
      )}
    </article>
  );
}

export function MyBusinessesPage() {
  const { t } = useTranslation();
  return (
    <div className="bl-page">
      <HubTitle title={t('businesses.title', 'My businesses')} />
      <BusinessesGrid />
    </div>
  );
}
